import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';
import AdminProtectedRoute from '../components/auth/AdminProtectedRoute';

// Lazy load components
const HomePage = lazy(() => import('../pages/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const FlightsPage = lazy(() => import('../pages/flights/page'));
const FlightDetailsPage = lazy(() => import('../pages/flight-details/page'));
const HotelsPage = lazy(() => import('../pages/hotels/page'));
const ToursPage = lazy(() => import('../pages/tours/page'));
const VisasPage = lazy(() => import('../pages/visas/page'));
const CargoPage = lazy(() => import('../pages/cargo/page'));
const InsurancePage = lazy(() => import('../pages/insurance/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const ContactConsultantPage = lazy(() => import('../pages/contact-consultant/page'));
const CorporatePage = lazy(() => import('../pages/corporate/page'));
const BookNowPage = lazy(() => import('../pages/book-now/page'));
const BookingReviewPage = lazy(() => import('../pages/booking-review/page'));
const BookingConfirmationPage = lazy(() => import('../pages/booking-confirmation/page'));
const ChangeBookingPage = lazy(() => import('../pages/change-booking/page'));
const AnalyticsPage = lazy(() => import('../pages/analytics/page'));
const PrivacyPolicyPage = lazy(() => import('../pages/privacy-policy/page'));
const TermsConditionsPage = lazy(() => import('../pages/terms-conditions/page'));
const CookiesPolicyPage = lazy(() => import('../pages/cookies-policy/page'));
const NotFoundPage = lazy(() => import('../pages/not-found/page'));
const ErrorPage = lazy(() => import('../pages/not-found/error-page'));
const ResetPasswordPage = lazy(() => import('../pages/reset-password/page'));

// Client Area
const ClientAreaPage = lazy(() => import('../pages/client-area/page'));

// Admin Pages
const AdminLoginPage = lazy(() => import('../pages/admin/login/page'));
const AdminLayout = lazy(() => import('../pages/admin/page'));
const AdminDashboard = lazy(() => import('../pages/admin/dashboard/page'));
const AdminReservas = lazy(() => import('../pages/admin/reservas/page'));
const AdminVoos = lazy(() => import('../pages/admin/voos/page'));
const AdminServicos = lazy(() => import('../pages/admin/servicos/page'));
const AdminPagamentos = lazy(() => import('../pages/admin/pagamentos/page'));
const AdminClientes = lazy(() => import('../pages/admin/clientes/page'));
const AdminConteudo = lazy(() => import('../pages/admin/conteudo/page'));
const AdminPaginas = lazy(() => import('../pages/admin/paginas/page'));
const AdminPaginasEditar = lazy(() => import('../pages/admin/paginas/editar/page'));
const AdminRelatorios = lazy(() => import('../pages/admin/relatorios/page'));
const AdminSuporte = lazy(() => import('../pages/admin/suporte/page'));
const AdminConfiguracoes = lazy(() => import('../pages/admin/configuracoes/page'));

const HotelDetailsPage = lazy(() => import('../pages/hotels/details/page'));
const TourDetailsPage = lazy(() => import('../pages/tours/details/page'));
const VisaDetailsPage = lazy(() => import('../pages/visas/details/page'));
const InsuranceDetailsPage = lazy(() => import('../pages/insurance/details/page'));
const CorporateDetailsPage = lazy(() => import('../pages/corporate/details/page'));

// Installation Page
const InstallPage = lazy(() => import('../pages/install/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/sobre',
    element: <AboutPage />,
  },
  {
    path: '/voos',
    element: <FlightsPage />,
  },
  {
    path: '/voos/detalhes',
    element: <FlightDetailsPage />,
  },
  {
    path: '/hoteis',
    element: <HotelsPage />,
  },
  {
    path: '/hoteis/detalhes',
    element: <HotelDetailsPage />,
  },
  {
    path: '/tours',
    element: <ToursPage />,
  },
  {
    path: '/tours/detalhes',
    element: <TourDetailsPage />,
  },
  {
    path: '/vistos',
    element: <VisasPage />,
  },
  {
    path: '/vistos/detalhes',
    element: <VisaDetailsPage />,
  },
  {
    path: '/envio-mercadorias',
    element: <CargoPage />,
  },
  {
    path: '/seguro-viagem',
    element: <InsurancePage />,
  },
  {
    path: '/seguro-viagem/detalhes',
    element: <InsuranceDetailsPage />,
  },
  {
    path: '/contacto',
    element: <ContactPage />,
  },
  {
    path: '/falar-consultor',
    element: <ContactConsultantPage />,
  },
  {
    path: '/corporativo',
    element: <CorporatePage />,
  },
  {
    path: '/corporativo/detalhes',
    element: <CorporateDetailsPage />,
  },
  {
    path: '/reservar-agora',
    element: <BookNowPage />,
  },
  {
    path: '/revisao-reserva',
    element: <BookingReviewPage />,
  },
  {
    path: '/confirmacao-reserva',
    element: <BookingConfirmationPage />,
  },
  {
    path: '/alterar-reserva',
    element: <ChangeBookingPage />,
  },
  {
    path: '/analytics',
    element: <AnalyticsPage />,
  },
  {
    path: '/politica-privacidade',
    element: <PrivacyPolicyPage />,
  },
  {
    path: '/termos-condicoes',
    element: <TermsConditionsPage />,
  },
  {
    path: '/politica-cookies',
    element: <CookiesPolicyPage />,
  },
  {
    path: '/area-cliente',
    element: <ClientAreaPage />,
  },
  {
    path: '/area-cliente/redefinir-senha',
    element: <ResetPasswordPage />,
  },
  {
    path: '/area-cliente/*',
    element: <ClientAreaPage />,
  },
  {
    path: '/install',
    element: <InstallPage />,
  },
  {
    path: '/erro',
    element: <ErrorPage />,
  },
  {
    path: '/admin/login',
    element: <AdminLoginPage />,
  },
  {
    path: '/admin',
    element: (
      <AdminProtectedRoute>
        <AdminLayout />
      </AdminProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <AdminDashboard />,
      },
      {
        path: 'dashboard',
        element: <AdminDashboard />,
      },
      {
        path: 'reservas',
        element: <AdminReservas />,
      },
      {
        path: 'voos',
        element: <AdminVoos />,
      },
      {
        path: 'servicos',
        element: <AdminServicos />,
      },
      {
        path: 'pagamentos',
        element: <AdminPagamentos />,
      },
      {
        path: 'clientes',
        element: <AdminClientes />,
      },
      {
        path: 'conteudo',
        element: <AdminConteudo />,
      },
      {
        path: 'paginas',
        element: <AdminPaginas />,
      },
      {
        path: 'paginas/editar',
        element: <AdminPaginasEditar />,
      },
      {
        path: 'relatorios',
        element: <AdminRelatorios />,
      },
      {
        path: 'suporte',
        element: <AdminSuporte />,
      },
      {
        path: 'configuracoes',
        element: <AdminConfiguracoes />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;
